// src/services/notificacoesService.js
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  getDoc,
  doc,
  updateDoc,
  serverTimestamp,
  onSnapshot,
} from 'firebase/firestore';

import { db, auth } from './firebase';

/* =========================================================
  Helpers
========================================================= */

function toMs(v) {
  return v?.toMillis
    ? v.toMillis()
    : typeof v?.seconds === 'number'
      ? v.seconds * 1000
      : typeof v === 'number'
        ? v
        : typeof v === 'string'
          ? new Date(v).getTime()
          : v instanceof Date
            ? v.getTime()
            : 0;
}

/**
 * Normaliza doc NOVO: notificacoes/{uid}/items/{id}
 * Campos esperados (novo):
 * - type | tipo
 * - title | mensagem
 * - read (boolean)
 * - createdAt / updatedAt / lastNotifiedAt
 */
function normalizeNotificacaoNew(id, data) {
  const n = data || {};
  const dateField =
    n.updatedAt || n.createdAt || n.lastNotifiedAt || n.data || null;

  return {
    id,
    ...n,
    // compat UI atual
    tipo: n.tipo || n.type || '',
    mensagem: n.mensagem || n.title || '',
    visualizado:
      typeof n.visualizado === 'boolean'
        ? n.visualizado
        : typeof n.read === 'boolean'
          ? n.read
          : false,
    data: dateField,
  };
}

/**
 * Normaliza doc LEGADO: notificacoes/{id}
 * Campos esperados (legado):
 * - tipo
 * - mensagem
 * - visualizado
 * - data
 * - userId / destinatarioUid
 */
function normalizeNotificacaoLegacy(id, data) {
  const n = data || {};
  const dateField = n.data || n.createdAt || n.criadoEm || n.criado_em || null;

  return {
    id,
    ...n,
    // compat UI atual
    tipo: n.tipo || '',
    mensagem: n.mensagem || n.title || '',
    visualizado: !!n.visualizado,
    data: dateField,
  };
}

/**
 * Paths
 */
function newItemsCol(uid) {
  return collection(db, 'notificacoes', uid, 'items');
}

function newItemDoc(uid, id) {
  return doc(db, 'notificacoes', uid, 'items', String(id));
}

function legacyCol() {
  return collection(db, 'notificacoes');
}

function legacyDoc(id) {
  return doc(db, 'notificacoes', String(id));
}

/* =========================================================
  API
========================================================= */

/**
 * Busca notificações do usuário logado.
 * ✅ Primeiro tenta NOVO (subcoleção /items) — SEM índice composto.
 * ⛑️ Fallback legado (sem orderBy pra não pedir índice).
 */
export async function getNotificacoes({
  max = 200,
  includeLegacyFallback = true,
} = {}) {
  const currentUser = auth.currentUser;
  if (!currentUser) return [];

  const uid = currentUser.uid;

  // -------------------------
  // 1) NOVO (recomendado)
  // -------------------------
  try {
    const qNew = query(
      newItemsCol(uid),
      orderBy('updatedAt', 'desc'),
      limit(max)
    );
    const snap = await getDocs(qNew);

    const items = snap.docs.map((d) => normalizeNotificacaoNew(d.id, d.data()));
    // ordena por segurança (caso algum doc não tenha updatedAt)
    items.sort((a, b) => toMs(b.data) - toMs(a.data));
    return items;
  } catch (err) {
    // Se a sua coleção nova ainda não tiver índice simples de updatedAt, isso é raro,
    // mas pode acontecer se updatedAt não existir em muitos docs.
    console.warn(
      '[getNotificacoes] falha no novo caminho:',
      err?.message || err
    );
  }

  // -------------------------
  // 2) LEGADO (opcional)
  //    IMPORTANTe: sem orderBy, para NÃO exigir índice composto.
  // -------------------------
  if (!includeLegacyFallback) return [];

  try {
    const colRef = legacyCol();

    const q1 = query(colRef, where('userId', '==', uid), limit(max));
    const q2 = query(colRef, where('destinatarioUid', '==', uid), limit(max));

    const [s1, s2] = await Promise.all([getDocs(q1), getDocs(q2)]);

    const map = new Map();
    s1.docs.forEach((d) =>
      map.set(d.id, normalizeNotificacaoLegacy(d.id, d.data()))
    );
    s2.docs.forEach((d) =>
      map.set(d.id, normalizeNotificacaoLegacy(d.id, d.data()))
    );

    const arr = Array.from(map.values());
    arr.sort((a, b) => toMs(b.data) - toMs(a.data));
    return arr;
  } catch (err) {
    console.error('[getNotificacoes] falha no legado:', err?.message || err);
    return [];
  }
}

/**
 * Marca como visualizada (true) / read (true).
 * ✅ Primeiro tenta NOVO (subcoleção /items)
 * ⛑️ Depois cai no LEGADO.
 */
export async function marcarComoVisualizada(id) {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Usuário não autenticado.');
  if (!id) throw new Error('ID da notificação é obrigatório.');

  const uid = currentUser.uid;

  // 1) novo
  try {
    const refNew = newItemDoc(uid, id);
    const snapNew = await getDoc(refNew);

    if (snapNew.exists()) {
      await updateDoc(refNew, {
        read: true,
        visualizado: true, // mantém compat com UI antiga se você usar
        visualizadoEm: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      return;
    }
  } catch (err) {
    console.warn('[marcarComoVisualizada] falha no novo:', err?.message || err);
  }

  // 2) legado
  const ref = legacyDoc(id);
  const snap = await getDoc(ref);
  if (!snap.exists()) return;

  await updateDoc(ref, {
    visualizado: true,
    visualizadoEm: serverTimestamp(),
  });
}

/**
 * Listener realtime
 * ✅ NOVO: /notificacoes/{uid}/items orderBy(updatedAt)
 * ⛑️ Fallback legado opcional (sem orderBy, sem índice)
 */
export function listenNotificacoes(
  onChange,
  { max = 200, includeLegacyFallback = false } = {}
) {
  const currentUser = auth.currentUser;
  if (!currentUser) return () => {};

  const uid = currentUser.uid;

  // -------- NOVO --------
  const qNew = query(
    newItemsCol(uid),
    orderBy('updatedAt', 'desc'),
    limit(max)
  );

  const unsubNew = onSnapshot(
    qNew,
    (snap) => {
      const items = snap.docs.map((d) =>
        normalizeNotificacaoNew(d.id, d.data())
      );
      items.sort((a, b) => toMs(b.data) - toMs(a.data));
      onChange?.(items);
    },
    (err) => {
      console.warn('[listenNotificacoes] falha no novo:', err?.message || err);
      // se quiser fallback automático, você pode ligar includeLegacyFallback
    }
  );

  if (!includeLegacyFallback) return () => unsubNew?.();

  // -------- LEGADO (opcional) --------
  // Sem orderBy pra não pedir índice composto.
  const map = new Map();

  const emit = () => {
    const arr = Array.from(map.values());
    arr.sort((a, b) => toMs(b.data) - toMs(a.data));
    onChange?.(arr);
  };

  const colRef = legacyCol();
  const q1 = query(colRef, where('userId', '==', uid), limit(max));
  const q2 = query(colRef, where('destinatarioUid', '==', uid), limit(max));

  const unsub1 = onSnapshot(q1, (snap) => {
    snap.docs.forEach((d) =>
      map.set(d.id, normalizeNotificacaoLegacy(d.id, d.data()))
    );
    emit();
  });

  const unsub2 = onSnapshot(q2, (snap) => {
    snap.docs.forEach((d) =>
      map.set(d.id, normalizeNotificacaoLegacy(d.id, d.data()))
    );
    emit();
  });

  return () => {
    unsubNew?.();
    unsub1?.();
    unsub2?.();
  };
}
